import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { scansApi, urlsApi } from '../services/api';
import './Home.css';

const SCAN_REASONS = [
    { value: '', label: 'Select a reason (optional)' },
    { value: 'page_wont_load', label: 'Page won\'t load' },
    { value: 'form_not_working', label: 'Form not working' },
    { value: 'image_not_showing', label: 'Image not showing' },
    { value: 'checkout_issue', label: 'Checkout issue' },
    { value: 'layout_broken', label: 'Layout broken' },
    { value: 'analytics_not_tracking', label: 'Analytics not tracking' },
    { value: 'performance_issue', label: 'Performance issue' },
    { value: 'other', label: 'Other' },
];

const DEVICES = [
    { value: 'desktop', label: 'Desktop' },
    { value: 'mobile', label: 'Mobile' },
    { value: 'both', label: 'Both' },
];

const LOCATIONS = [
    { value: 'auto', label: 'Auto (Nearest)' },
    { value: 'us', label: 'United States' },
    { value: 'eu', label: 'Europe' },
    { value: 'apac', label: 'Asia Pacific' },
];

interface UrlSuggestion {
    url: string;
    last_used: string;
}

export default function Home() {
    const navigate = useNavigate();
    const [url, setUrl] = useState('');
    const [mode, setMode] = useState<'quick' | 'guided'>('quick');
    const [device, setDevice] = useState('desktop');
    const [location, setLocation] = useState('auto');
    const [scanReason, setScanReason] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Autocomplete state
    const [suggestions, setSuggestions] = useState<UrlSuggestion[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);

    // Fetch suggestions when URL changes
    useEffect(() => {
        const fetchSuggestions = async () => {
            try {
                const data = await urlsApi.getRecent(url || undefined);
                setSuggestions(data);
            } catch (err) {
                console.error('Failed to fetch suggestions:', err);
            }
        };

        const debounce = setTimeout(fetchSuggestions, 150);
        return () => clearTimeout(debounce);
    }, [url]);

    // Close suggestions on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                suggestionsRef.current &&
                !suggestionsRef.current.contains(e.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(e.target as Node)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!showSuggestions || suggestions.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex((prev) => Math.max(prev - 1, -1));
        } else if (e.key === 'Enter' && selectedIndex >= 0) {
            e.preventDefault();
            selectSuggestion(suggestions[selectedIndex].url);
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    const selectSuggestion = (selectedUrl: string) => {
        setUrl(selectedUrl);
        setShowSuggestions(false);
        setSelectedIndex(-1);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setShowSuggestions(false);

        if (!url.trim()) {
            setError('Please enter a URL');
            return;
        }

        try {
            let normalizedUrl = url.trim();
            if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
                normalizedUrl = 'https://' + normalizedUrl;
            }
            new URL(normalizedUrl);

            setIsLoading(true);

            const result = await scansApi.create({
                url: normalizedUrl,
                device,
                location,
                scanReason: scanReason || undefined,
            });

            navigate(`/loading/${result.scanId}`);
        } catch (err) {
            if (err instanceof TypeError) {
                setError('Please enter a valid URL');
            } else {
                setError('Failed to start scan. Please try again.');
            }
            setIsLoading(false);
        }
    };

    const formatUrl = (fullUrl: string) => {
        try {
            const parsed = new URL(fullUrl);
            return parsed.hostname + (parsed.pathname !== '/' ? parsed.pathname : '');
        } catch {
            return fullUrl;
        }
    };

    return (
        <div className="home">
            <div className="home-container">
                {/* Hero Section */}
                <div className="hero">
                    <h1 className="hero-title">Debug Any Website in Seconds</h1>
                    <p className="tagline">
                        Clinical, evidence-based debugging for real-world web failures
                    </p>
                </div>

                {/* Scan Form */}
                <div className="scan-card">
                    {/* Mode Toggle */}
                    <div className="mode-toggle">
                        <button
                            className={`mode-btn ${mode === 'quick' ? 'active' : ''}`}
                            onClick={() => setMode('quick')}
                        >
                            Quick Scan
                        </button>
                        <button
                            className={`mode-btn ${mode === 'guided' ? 'active' : ''}`}
                            onClick={() => setMode('guided')}
                        >
                            Guided Scan
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        {/* URL Input with Autocomplete */}
                        <div className="url-input-group">
                            <div className="url-input-wrapper">
                                <input
                                    ref={inputRef}
                                    type="text"
                                    className="url-input"
                                    placeholder="Enter URL to analyze..."
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    onFocus={() => setShowSuggestions(true)}
                                    onKeyDown={handleKeyDown}
                                    disabled={isLoading}
                                    autoComplete="off"
                                />
                                {showSuggestions && suggestions.length > 0 && (
                                    <div ref={suggestionsRef} className="suggestions-dropdown">
                                        {suggestions.map((suggestion, index) => (
                                            <button
                                                key={suggestion.url}
                                                type="button"
                                                className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
                                                onClick={() => selectSuggestion(suggestion.url)}
                                            >
                                                <span className="suggestion-icon">→</span>
                                                <span className="suggestion-url">{formatUrl(suggestion.url)}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button type="submit" className="scan-btn" disabled={isLoading}>
                                {isLoading ? (
                                    <>
                                        <span className="spinner"></span>
                                        Scanning...
                                    </>
                                ) : (
                                    <>
                                        <span>▶</span>
                                        Run Scan
                                    </>
                                )}
                            </button>
                        </div>

                        {error && <div className="error-message">{error}</div>}

                        {/* Guided Options */}
                        {mode === 'guided' && (
                            <div className="guided-options">
                                <div className="options-grid">
                                    <div className="option-group">
                                        <label className="label">Device</label>
                                        <select
                                            className="select"
                                            value={device}
                                            onChange={(e) => setDevice(e.target.value)}
                                        >
                                            {DEVICES.map((d) => (
                                                <option key={d.value} value={d.value}>
                                                    {d.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="option-group">
                                        <label className="label">Location</label>
                                        <select
                                            className="select"
                                            value={location}
                                            onChange={(e) => setLocation(e.target.value)}
                                        >
                                            {LOCATIONS.map((l) => (
                                                <option key={l.value} value={l.value}>
                                                    {l.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="option-group full-width">
                                        <label className="label">What's the issue?</label>
                                        <select
                                            className="select"
                                            value={scanReason}
                                            onChange={(e) => setScanReason(e.target.value)}
                                        >
                                            {SCAN_REASONS.map((r) => (
                                                <option key={r.value} value={r.value}>
                                                    {r.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                    </form>
                </div>

                {/* Features */}
                <div className="features">
                    <div className="feature">
                        <h3>Visual Evidence</h3>
                        <p>Screenshots and recordings capture exactly what went wrong</p>
                    </div>
                    <div className="feature">
                        <h3>Deep Analysis</h3>
                        <p>Network, console, and DOM inspection reveal hidden issues</p>
                    </div>
                    <div className="feature">
                        <h3>Clinical Reports</h3>
                        <p>Evidence-based findings with severity and confidence levels</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

